import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Card, Alert, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';
import SetLogger from './SetLogger';
import CardioLogger from './CardioLogger';
import RestBlockCard from './RestBlockCard';
import ExerciseForm from './ExerciseForm';
import { usesSets, isStretch, hasReps, hasDuration, EXERCISE_TYPE_LABELS } from '../utils/exerciseTypes';

// The swap picker lives in a modal nested inside this one, which clips its own
// overflow - an in-place menu is cut off at the dialog edge. Portalling to body
// with a z-index above Bootstrap's 1055 keeps the list usable.
const PICKER_MENU_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

// One blank set, shaped by exercise_type. `carryOver` is only passed on a swap:
// weights, notes and any column the new type still offers survive, the rest fall
// away as undefined so handleSubmit never sends a column the type has no home for.
const blankSet = (exerciseType, setNumber = 1, carryOver = null) => ({
  set_number: setNumber,
  weight: carryOver?.weight ?? '',
  weight_unit: carryOver?.weight_unit || 'lbs',
  resistance: isStretch(exerciseType) ? (carryOver?.resistance ?? '') : undefined,
  reps: hasReps(exerciseType) ? (carryOver?.reps ?? '') : undefined,
  reps_prescription: exerciseType === 'weight_reps' ? (carryOver?.reps_prescription ?? '') : undefined,
  duration_seconds: hasDuration(exerciseType) ? (carryOver?.duration_seconds ?? '') : undefined,
  notes: carryOver?.notes ?? ''
});

// Sets for an exercise of `exerciseType`. Adding an exercise passes nothing and
// gets a single blank set; swapping to a different type passes the old sets, so
// the prescription's shape (how many sets, at what weight) survives the change.
const buildSetsForType = (exerciseType, existingSets = []) => {
  if (!usesSets(exerciseType)) return [];
  if (existingSets.length === 0) return [blankSet(exerciseType)];
  return existingSets.map((set, i) => blankSet(exerciseType, i + 1, set));
};

// An item is a rest block whether it came from the API (item_type) or was added
// in this session (type).
const isRestItem = (item) => item.item_type === 'rest_block' || item.type === 'rest_block';

function TemplateForm({ show, onHide, template, onSave, error }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    notes: '',
    exercises: [] // Array of items (exercises, supersets, rest blocks)
  });

  const [errors, setErrors] = useState({});
  const [availableExercises, setAvailableExercises] = useState([]);
  const [isLoadingExercises, setIsLoadingExercises] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  // Exercise creation modal
  const [showExerciseForm, setShowExerciseForm] = useState(false);
  const [isSavingExercise, setIsSavingExercise] = useState(false);

  // Swap (replace an exercise in place, keeping its slot in the template)
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null); // { index, exercise_id, exercise_type, exercise_name }
  const [swapSelection, setSwapSelection] = useState(null);

  // Superset creation
  const [selectedExerciseIds, setSelectedExerciseIds] = useState(new Set());
  const [showSupersetModal, setShowSupersetModal] = useState(false);

  // Drag and drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (show) {
      fetchExercises();
    }
  }, [show]);

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        description: template.description || '',
        notes: template.notes || '',
        exercises: (template.exercises || []).map((ex, index) => {
          const item = {
            ...ex,
            type: ex.item_type || 'exercise',
            id: Date.now() + index
          };

          // Handle rest blocks
          if (ex.item_type === 'rest_block') {
            item.rest_duration_seconds = ex.rest_duration_seconds || 60;
            item.rest_notes = ex.rest_notes || ex.notes || '';
          } else {
            // Handle exercises
            item.notes = ex.notes || '';
            // Convert null values to empty strings in sets
            item.sets = ex.sets ? ex.sets.map(set => ({
              ...set,
              weight: set.weight || '',
              reps: set.reps || '',
              reps_prescription: set.reps_prescription || '',
              duration_seconds: set.duration_seconds || '',
              notes: set.notes || ''
            })) : (usesSets(ex.exercise_type) ? [{
              set_number: 1,
              weight: '',
              weight_unit: 'lbs',
              resistance: isStretch(ex.exercise_type) ? '' : undefined,
              reps: hasReps(ex.exercise_type) ? '' : undefined,
              reps_prescription: ex.exercise_type === 'weight_reps' ? '' : undefined,
              duration_seconds: hasDuration(ex.exercise_type) ? '' : undefined,
              notes: ''
            }] : []);
            item.cardio_data = ex.cardio_data || null;
            item.cardio_segments = ex.cardio_segments || [];
          }

          return item;
        })
      });
    } else {
      setFormData({
        name: '',
        description: '',
        notes: '',
        exercises: []
      });
    }
    setErrors({});
    setSelectedExerciseIds(new Set());
  }, [template, show]);

  const fetchExercises = async () => {
    setIsLoadingExercises(true);
    try {
      const data = await workoutAPI.fetchExercises(false);
      setAvailableExercises(data);
    } catch (err) {
      console.error('Error fetching exercises:', err);
    } finally {
      setIsLoadingExercises(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddExercise = () => {
    if (!selectedExercise) return;

    const newExercise = {
      type: 'exercise',
      item_type: 'exercise',
      id: Date.now(),
      exercise_id: selectedExercise.value,
      exercise_name: selectedExercise.label,
      exercise_type: selectedExercise.exercise_type,
      category: selectedExercise.category,
      notes: '',
      sets: buildSetsForType(selectedExercise.exercise_type),
      cardio_data: null,
      cardio_segments: []
    };

    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, newExercise]
    }));
    setSelectedExercise(null);
  };

  const handleAddRestBlock = () => {
    const newRestBlock = {
      type: 'rest_block',
      item_type: 'rest_block',
      id: Date.now(),
      rest_duration_seconds: 60,
      rest_notes: ''
    };

    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, newRestBlock]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
    // Clear any selection
    setSelectedExerciseIds(new Set());
  };

  const handleOpenSwapModal = (index) => {
    const item = formData.exercises[index];
    if (!item) return;
    setSwapTarget({
      index,
      exercise_id: item.exercise_id,
      exercise_type: item.exercise_type,
      exercise_name: item.exercise_name
    });
    setSwapSelection(null);
    setShowSwapModal(true);
  };

  const handleCloseSwapModal = () => {
    setShowSwapModal(false);
    setSwapTarget(null);
    setSwapSelection(null);
  };

  const handleConfirmSwap = () => {
    if (!swapTarget || !swapSelection) return;

    const newType = swapSelection.exercise_type;
    const sameType = newType === swapTarget.exercise_type;

    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((item, index) => {
        if (index !== swapTarget.index) return item;

        // Only the exercise's identity changes. Position, notes, superset
        // membership and item_type all ride along on the spread - that is the
        // whole point of a swap over delete-and-re-add.
        const swapped = {
          ...item,
          exercise_id: swapSelection.value,
          exercise_name: swapSelection.label,
          exercise_type: newType,
          category: swapSelection.category || null
        };

        // A different type is a different shape, so rebuild what the new type
        // actually offers and drop the cardio rows that no longer apply.
        if (!sameType) {
          swapped.sets = buildSetsForType(newType, item.sets || []);
          swapped.cardio_data = null;
          swapped.cardio_segments = [];
        }

        return swapped;
      })
    }));

    handleCloseSwapModal();
  };

  const handleUpdateExerciseSets = (index, data) => {
    const newExercises = [...formData.exercises];
    newExercises[index].sets = data.sets;
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  const handleUpdateExerciseCardio = (index, data) => {
    const newExercises = [...formData.exercises];
    if (data.cardio_data !== undefined) {
      newExercises[index].cardio_data = data.cardio_data;
    }
    if (data.cardio_segments !== undefined) {
      newExercises[index].cardio_segments = data.cardio_segments;
    }
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  const handleUpdateExerciseNotes = (index, notes) => {
    const newExercises = [...formData.exercises];
    newExercises[index].notes = notes;
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newExercises = [...formData.exercises];
    const draggedItem = newExercises[draggedIndex];

    // Check if dragging a superset - need to move all exercises in the superset together
    if (draggedItem.superset_group_id) {
      const supersetGroupId = draggedItem.superset_group_id;
      const supersetExercises = newExercises.filter(ex => ex.superset_group_id === supersetGroupId);
      const nonSupersetExercises = newExercises.filter(ex => ex.superset_group_id !== supersetGroupId);

      // Find the position to insert
      const dropItem = newExercises[dropIndex];
      let insertIndex;

      if (dropItem.superset_group_id === supersetGroupId) {
        // Dropping on itself, do nothing
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      // Find position in non-superset array
      insertIndex = nonSupersetExercises.findIndex(ex =>
        newExercises.indexOf(ex) === dropIndex
      );

      if (insertIndex === -1) {
        insertIndex = nonSupersetExercises.length;
      }

      // Insert superset exercises at the new position
      nonSupersetExercises.splice(insertIndex, 0, ...supersetExercises);

      setFormData(prev => ({
        ...prev,
        exercises: nonSupersetExercises
      }));
    } else {
      // Single item drag
      newExercises.splice(draggedIndex, 1);
      newExercises.splice(dropIndex, 0, draggedItem);

      setFormData(prev => ({
        ...prev,
        exercises: newExercises
      }));
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleUpdateRestBlock = (index, updatedData) => {
    const newExercises = [...formData.exercises];
    newExercises[index] = { ...newExercises[index], ...updatedData };
    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  // Superset functionality
  const handleToggleExerciseSelection = (index) => {
    const newSelected = new Set(selectedExerciseIds);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedExerciseIds(newSelected);
  };

  const handleCreateSuperset = () => {
    const selectedIndices = Array.from(selectedExerciseIds).sort((a, b) => a - b);
    const selectedItems = selectedIndices.map(index => formData.exercises[index]);

    // Rest blocks pace the chain but don't make it a superset on their own.
    if (selectedItems.filter(item => !isRestItem(item)).length < 2) {
      alert('Select at least 2 exercises to create a superset');
      return;
    }

    const supersetGroupId = Date.now();
    const grouped = selectedItems.map(item => ({
      ...item,
      superset_group_id: supersetGroupId,
      item_type: isRestItem(item) ? 'rest_block' : 'superset'
    }));

    // Collect the chain at the first selected slot so on-screen order is saved order.
    const insertAt = selectedIndices[0];
    const selectedSet = new Set(selectedIndices);
    const newExercises = [];
    formData.exercises.forEach((item, index) => {
      if (index === insertAt) newExercises.push(...grouped);
      if (!selectedSet.has(index)) newExercises.push(item);
    });

    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));

    setSelectedExerciseIds(new Set());
  };

  const handleBreakSuperset = (supersetGroupId) => {
    const newExercises = formData.exercises.map(ex => {
      if (ex.superset_group_id === supersetGroupId) {
        return {
          ...ex,
          superset_group_id: null,
          // A rest block stays a rest block once the chain is broken.
          item_type: isRestItem(ex) ? 'rest_block' : 'exercise'
        };
      }
      return ex;
    });

    setFormData(prev => ({
      ...prev,
      exercises: newExercises
    }));
  };

  // Create new exercise
  const handleCreateExercise = async (exerciseData) => {
    setIsSavingExercise(true);
    try {
      const newExercise = await workoutAPI.createExercise(exerciseData);
      await fetchExercises();
      setShowExerciseForm(false);
      // Auto-select the new exercise
      const exerciseOption = {
        value: newExercise.id,
        label: newExercise.name,
        exercise_type: newExercise.exercise_type,
        category: newExercise.category
      };
      setSelectedExercise(exerciseOption);
    } catch (err) {
      console.error('Error creating exercise:', err);
    } finally {
      setIsSavingExercise(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (formData.exercises.length === 0) {
      newErrors.exercises = 'Add at least one exercise or rest block to the template';
    } else if (formData.exercises.every(isRestItem)) {
      newErrors.exercises = 'A template needs at least one exercise, not just rest blocks';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      // Group ids are generated with Date.now(), which overflows the INT column -
      // renumber them 1..N on the way out so each superset keeps its own id.
      const groupNumbers = new Map();
      formData.exercises.forEach((item) => {
        if (item.superset_group_id && !groupNumbers.has(item.superset_group_id)) {
          groupNumbers.set(item.superset_group_id, groupNumbers.size + 1);
        }
      });

      // Format exercises for backend
      const formattedExercises = formData.exercises.map((item, index) => {
        const formatted = {
          item_type: item.item_type || item.type || 'exercise',
          sort_order: index,
          // Rest blocks can sit inside a chain, so this belongs on every item.
          superset_group_id: groupNumbers.get(item.superset_group_id) || null
        };

        if (isRestItem(item)) {
          // Rest block
          formatted.rest_duration_seconds = item.rest_duration_seconds || 60;
          formatted.notes = item.rest_notes || '';
        } else {
          // Exercise
          formatted.exercise_id = item.exercise_id;
          formatted.notes = item.notes || '';
          formatted.sets = item.sets || [];
          formatted.cardio_data = item.cardio_data;
          formatted.cardio_segments = item.cardio_segments || [];
        }

        return formatted;
      });

      const dataToSave = {
        name: formData.name,
        description: formData.description,
        notes: formData.notes,
        exercises: formattedExercises
      };

      await onSave(dataToSave);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare exercise options for select dropdown
  const exerciseOptions = availableExercises.map(ex => ({
    value: ex.id,
    label: ex.name,
    exercise_type: ex.exercise_type,
    category: ex.category
  }));

  // Grouped for the picker so stretches sit in their own section rather than
  // interleaved alphabetically with lifts.
  const groupedExerciseOptions = [
    { label: 'Exercises', options: exerciseOptions.filter(o => !isStretch(o.exercise_type)) },
    { label: 'Stretches', options: exerciseOptions.filter(o => isStretch(o.exercise_type)) }
  ].filter(group => group.options.length > 0);

  // The swap picker offers every exercise, not just same-type ones the way the
  // Logger's Substitute does. A template holds a prescription rather than logged
  // data, so a type change costs nothing that cannot be warned about and rebuilt.
  const swapOptions = swapTarget
    ? groupedExerciseOptions
        .map(group => ({
          ...group,
          options: group.options.filter(o => o.value !== swapTarget.exercise_id)
        }))
        .filter(group => group.options.length > 0)
    : [];

  const swapChangesType = !!(swapSelection && swapTarget && swapSelection.exercise_type !== swapTarget.exercise_type);

  // Group exercises by superset
  const groupedExercises = [];
  const processedSupersets = new Set();

  formData.exercises.forEach((item, index) => {
    if (item.superset_group_id && !processedSupersets.has(item.superset_group_id)) {
      // Find all exercises in this superset
      const supersetExercises = formData.exercises
        .map((ex, idx) => ({ ...ex, originalIndex: idx }))
        .filter(ex => ex.superset_group_id === item.superset_group_id);

      groupedExercises.push({
        type: 'superset',
        id: item.superset_group_id,
        exercises: supersetExercises
      });

      processedSupersets.add(item.superset_group_id);
    } else if (!item.superset_group_id) {
      groupedExercises.push({
        type: item.item_type || item.type || 'exercise',
        ...item,
        originalIndex: index
      });
    }
  });

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" fullscreen="lg-down" backdrop="static" scrollable dialogClassName="template-form-modal-wide">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-alt me-2"></i>
            {template ? 'Edit Template' : 'Create New Template'}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {error && (
              <Alert variant="danger">
                {error}
              </Alert>
            )}

            {/* Template Name */}
            <Form.Group className="mb-3">
              <Form.Label>
                Template Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
                placeholder="e.g., Upper Body Push, Leg Day, Full Body"
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of this workout template"
              />
            </Form.Group>

            {/* Add Exercises Toolbar */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="row g-2">
                <div className="col-md-6">
                  <Select
                    options={groupedExerciseOptions}
                    onChange={setSelectedExercise}
                    value={selectedExercise}
                    isLoading={isLoadingExercises}
                    placeholder="Select an exercise to add..."
                    isClearable
                  />
                </div>
                <div className="col-md-6 d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleAddExercise}
                    disabled={!selectedExercise}
                  >
                    <i className="fas fa-plus me-1"></i>
                    Add Exercise
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowExerciseForm(true)}
                  >
                    <i className="fas fa-plus-circle me-1"></i>
                    New Exercise
                  </Button>
                  <Button
                    variant="warning"
                    onClick={handleAddRestBlock}
                  >
                    <i className="fas fa-pause me-1"></i>
                    Add Rest
                  </Button>
                </div>
              </div>

              {/* Superset controls */}
              {selectedExerciseIds.size > 0 && (
                <div className="mt-2">
                  <Badge bg="info" className="me-2">
                    {selectedExerciseIds.size} selected
                  </Badge>
                  <Button
                    variant="info"
                    size="sm"
                    onClick={handleCreateSuperset}
                    disabled={
                      Array.from(selectedExerciseIds)
                        .filter((index) => !isRestItem(formData.exercises[index] || {})).length < 2
                    }
                  >
                    <i className="fas fa-link me-1"></i>
                    Create Superset
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={() => setSelectedExerciseIds(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {errors.exercises && (
                <div className="text-danger small mt-2">{errors.exercises}</div>
              )}
            </div>

            {/* Exercises List */}
            <div className="template-exercises-list">
              {groupedExercises.map((item, groupIndex) => {
                if (item.type === 'superset') {
                  // Use first exercise's index for dragging
                  const firstExerciseIndex = item.exercises[0].originalIndex;
                  return (
                    <Card
                      key={item.id}
                      className={`mb-3 superset-card ${draggedIndex === firstExerciseIndex ? 'dragging' : ''} ${dragOverIndex === firstExerciseIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, firstExerciseIndex)}
                      onDragOver={(e) => handleDragOver(e, firstExerciseIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, firstExerciseIndex)}
                    >
                      <Card.Header className="bg-info text-white">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span className="drag-handle me-2">
                              <i className="fas fa-grip-vertical"></i>
                            </span>
                            <h6 className="mb-0">
                              <i className="fas fa-link me-2"></i>
                              Superset ({item.exercises.filter((ex) => !isRestItem(ex)).length} exercises
                              {item.exercises.some(isRestItem) &&
                                ` + ${item.exercises.filter(isRestItem).length} rest`})
                            </h6>
                          </div>
                          <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => handleBreakSuperset(item.id)}
                          >
                            <i className="fas fa-unlink me-1"></i>
                            Break Superset
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {item.exercises.map((ex, subIndex) => (
                          <div key={ex.originalIndex} className="superset-exercise mb-3">
                            {isRestItem(ex) ? (
                              <>
                                <div className="d-flex align-items-center mb-2">
                                  <Badge bg="warning" className="me-2">
                                    {String.fromCharCode(65 + subIndex)}
                                  </Badge>
                                  <h6 className="mb-0">
                                    <i className="fas fa-pause-circle me-2"></i>
                                    Rest Block
                                  </h6>
                                </div>
                                <RestBlockCard
                                  restBlock={ex}
                                  onUpdate={(updatedData) => handleUpdateRestBlock(ex.originalIndex, updatedData)}
                                  onDelete={() => handleRemoveItem(ex.originalIndex)}
                                />
                              </>
                            ) : (
                              <>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <Badge bg="secondary" className="me-2">
                                      {String.fromCharCode(65 + subIndex)}
                                    </Badge>
                                    <strong>{ex.exercise_name}</strong>
                                    <Badge bg="primary" className="ms-2">{ex.exercise_type}</Badge>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleOpenSwapModal(ex.originalIndex)}
                                      title="Swap exercise"
                                    >
                                      <i className="fas fa-exchange-alt"></i>
                                      <span className="d-none d-sm-inline ms-1">Swap</span>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleRemoveItem(ex.originalIndex)}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </div>
                                </div>

                                {/* Set logger for weight exercises */}
                                {(usesSets(ex.exercise_type)) && (
                                  <SetLogger
                                    key={`${ex.exercise_id}-${ex.exercise_type}`}
                                    exercise={ex}
                                    onUpdate={(data) => handleUpdateExerciseSets(ex.originalIndex, data)}
                                    mode="template"
                                  />
                                )}

                                {/* Cardio logger */}
                                {(ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments') && (
                                  <CardioLogger
                                    key={`${ex.exercise_id}-${ex.exercise_type}`}
                                    exercise={ex}
                                    onUpdate={(data) => handleUpdateExerciseCardio(ex.originalIndex, data)}
                                  />
                                )}

                                <Form.Group className="mt-2">
                                  <Form.Control
                                    as="textarea"
                                    rows={1}
                                    size="sm"
                                    placeholder="Exercise notes (optional)"
                                    value={ex.notes || ''}
                                    onChange={(e) => handleUpdateExerciseNotes(ex.originalIndex, e.target.value)}
                                  />
                                </Form.Group>
                              </>
                            )}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  );
                } else if (item.type === 'rest_block' || item.item_type === 'rest_block') {
                  return (
                    <div
                      key={item.originalIndex}
                      className={`mb-3 ${draggedIndex === item.originalIndex ? 'dragging' : ''} ${dragOverIndex === item.originalIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.originalIndex)}
                      onDragOver={(e) => handleDragOver(e, item.originalIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, item.originalIndex)}
                    >
                      <RestBlockCard
                        restBlock={item}
                        onUpdate={(updatedData) => handleUpdateRestBlock(item.originalIndex, updatedData)}
                        onDelete={() => handleRemoveItem(item.originalIndex)}
                        selected={selectedExerciseIds.has(item.originalIndex)}
                        onToggleSelect={() => handleToggleExerciseSelection(item.originalIndex)}
                      />
                    </div>
                  );
                } else {
                  // Regular exercise
                  return (
                    <Card
                      key={item.originalIndex}
                      className={`mb-3 exercise-card ${draggedIndex === item.originalIndex ? 'dragging' : ''} ${dragOverIndex === item.originalIndex ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.originalIndex)}
                      onDragOver={(e) => handleDragOver(e, item.originalIndex)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, item.originalIndex)}
                    >
                      <Card.Header>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <span className="drag-handle me-2">
                              <i className="fas fa-grip-vertical"></i>
                            </span>
                            <Form.Check
                              type="checkbox"
                              className="me-2"
                              checked={selectedExerciseIds.has(item.originalIndex)}
                              onChange={() => handleToggleExerciseSelection(item.originalIndex)}
                            />
                            <strong>{item.exercise_name}</strong>
                            <Badge bg="primary" className="ms-2">{item.exercise_type}</Badge>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenSwapModal(item.originalIndex)}
                              title="Swap exercise"
                            >
                              <i className="fas fa-exchange-alt"></i>
                              <span className="d-none d-sm-inline ms-1">Swap</span>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveItem(item.originalIndex)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {/* Set logger for weight exercises */}
                        {(usesSets(item.exercise_type)) && (
                          <SetLogger
                            key={`${item.exercise_id}-${item.exercise_type}`}
                            exercise={item}
                            onUpdate={(data) => handleUpdateExerciseSets(item.originalIndex, data)}
                            mode="template"
                          />
                        )}

                        {/* Cardio logger */}
                        {(item.exercise_type === 'cardio_basic' || item.exercise_type === 'cardio_segments') && (
                          <CardioLogger
                            key={`${item.exercise_id}-${item.exercise_type}`}
                            exercise={item}
                            onUpdate={(data) => handleUpdateExerciseCardio(item.originalIndex, data)}
                          />
                        )}

                        <Form.Group className="mt-2">
                          <Form.Control
                            as="textarea"
                            rows={1}
                            size="sm"
                            placeholder="Exercise notes (optional)"
                            value={item.notes || ''}
                            onChange={(e) => handleUpdateExerciseNotes(item.originalIndex, e.target.value)}
                          />
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  );
                }
              })}

              {formData.exercises.length === 0 && (
                <Card className="text-center py-5 bg-light">
                  <Card.Body>
                    <i className="fas fa-dumbbell fa-3x text-muted mb-3"></i>
                    <p className="text-muted">
                      No exercises added yet. Add exercises above to build your template.
                    </p>
                  </Card.Body>
                </Card>
              )}
            </div>

            {/* Template Notes */}
            <Form.Group className="mt-3">
              <Form.Label>Template Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="General notes about this template"
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Template
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Swap Exercise Modal */}
      <Modal show={showSwapModal} onHide={handleCloseSwapModal} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exchange-alt me-2"></i>
            Swap Exercise
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {swapTarget && (
            <p className="text-muted small">
              Replacing <strong>{swapTarget.exercise_name}</strong> in this template.
            </p>
          )}

          <Form.Group>
            <Form.Label>Pick a replacement</Form.Label>
            <Select
              options={swapOptions}
              value={swapSelection}
              onChange={setSwapSelection}
              placeholder="Search for exercise..."
              isSearchable
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={PICKER_MENU_STYLES}
            />
          </Form.Group>

          {swapSelection && (
            <div className="mt-3">
              <p className="mb-1">
                <strong>Type:</strong> {EXERCISE_TYPE_LABELS[swapSelection.exercise_type] || swapSelection.exercise_type}
              </p>
              {swapSelection.category && (
                <p className="mb-0">
                  <strong>Category:</strong> {swapSelection.category}
                </p>
              )}
            </div>
          )}

          {swapChangesType ? (
            <Alert variant="warning" className="mt-3 mb-0 py-2 small">
              <i className="fas fa-exclamation-triangle me-1"></i>
              This changes the exercise type from{' '}
              <strong>{EXERCISE_TYPE_LABELS[swapTarget.exercise_type] || swapTarget.exercise_type}</strong> to{' '}
              <strong>{EXERCISE_TYPE_LABELS[swapSelection.exercise_type] || swapSelection.exercise_type}</strong>,
              so the prescribed sets will be rebuilt for the new type. Notes and position are kept.
            </Alert>
          ) : (
            <Alert variant="info" className="mt-3 mb-0 py-2 small">
              <i className="fas fa-info-circle me-1"></i>
              Prescribed sets, notes and superset grouping will be preserved.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSwapModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmSwap}
            disabled={!swapSelection}
          >
            <i className="fas fa-exchange-alt me-1"></i>
            Swap
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Exercise Creation Modal */}
      {showExerciseForm && (
        <ExerciseForm
          show={showExerciseForm}
          onHide={() => setShowExerciseForm(false)}
          exercise={null}
          onSave={handleCreateExercise}
          isLoading={isSavingExercise}
        />
      )}
    </>
  );
}

export default TemplateForm;
